import { useMutation, useQueryClient } from '@tanstack/react-query';
import { t } from 'i18next';

import { userAPI } from '../api/userAPI';
import { IUserUpdate } from '../types/User';
import { custonSooner } from '../../../components/CustopmSooner';
import { captureApiError, captureError } from '../../../lib/sentry-utils';

/**
 * @param successMessage — текст тоста после сохранения. По умолчанию общий
 * «данные обновлены»; экраны, которые правят одно поле, передают свой, чтобы
 * уведомление говорило именно о том, что изменилось. null — не показывать
 * тост вовсе (шаг регистрации: там об успехе говорит сам переход дальше).
 */
export const useUpdateUser = (successMessage?: string | null) => {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (body: IUserUpdate) => {
      try {
        const res = await userAPI.update(body);
        if (res.data && res.data.success === false) {
          captureError(res.data.message || 'Failed to update user profile', {
            endpoint: '/api/user/update',

            updateData: {
              email: body.email,

              gender: body.gender,
            },
            responseData: {
              success: res.data?.success,
              message: res.data?.message,
              error: res.data?.error,
            },
          });
        }
        return res;
      } catch (error) {
        console.error('UpdateUser error:', {
          message: error instanceof Error ? error.message : String(error),
          status: (error as any)?.response?.status,
          statusText: (error as any)?.response?.statusText,
          data: (error as any)?.response?.data
            ? {
                success: (error as any).response.data.success,
                message: (error as any).response.data.message,
                error: (error as any).response.data.error,
              }
            : undefined,
        });
        captureApiError(error, '/api/user/update', 'POST');
        throw error;
      }
    },
    onSuccess: res => {
      // Бэк отдаёт 200 и на отказ — success: false в теле. Без этой проверки
      // пользователь видел «сохранено» там, где ничего не сохранилось.
      if (res.data?.success === false) {
        custonSooner.error(res.data.message ?? t('somethingWentWrong'));
        return;
      }

      queryClient.invalidateQueries({ queryKey: ['user-profile'] });

      if (successMessage !== null) {
        custonSooner.success(successMessage ?? t('profileUpdated'));
      }
    },
    onError: error => {
      console.error('UpdateUser mutation error:', {
        message: error instanceof Error ? error.message : String(error),
        status: (error as any)?.response?.status,
        statusText: (error as any)?.response?.statusText,
        data: (error as any)?.response?.data
          ? {
              success: (error as any).response.data.success,
              message: (error as any).response.data.message,
              error: (error as any).response.data.error,
            }
          : undefined,
      });
      captureApiError(error, '/api/user/update', 'POST');
      custonSooner.error(t('somethingWentWrong'));
    },
  });
  return {
    updateUser: mutateAsync,
    isPending,
  };
};
